import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { HeaderService } from '../../../../services/header/header.service';
import { LogicielCaisseAuthService } from '../../../../services/logiciel-caisse/logiciel-caisse-auth.service';
import { LogicielCaisseTicketService } from '../../../../services/logiciel-caisse/logiciel-caisse-ticket.service';

export interface FactureConsolidTicketRow {
  id: number;
  numero: string;
  dateLabel: string;
  typeLabel: string;
  consolide: boolean;
  entrepot: string;
  statut: string;
  paiement: string;
  client: string;
  totalLabel: string;
}

@Component({
  selector: 'app-facture-consolid',
  templateUrl: './facture-consolid.component.html',
  styleUrls: ['./facture-consolid.component.scss'],
})
export class FactureConsolidComponent implements OnInit, OnDestroy {
  factureId = 0;
  factureCode: string | null = null;
  returnUrl = '/vente/facture/tous';
  loading = true;
  tickets: FactureConsolidTicketRow[] = [];
  skeletonRows = [0, 1, 2, 3, 4, 5];

  private querySub: Subscription | null = null;
  private loadSub: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly authService: LogicielCaisseAuthService,
    private readonly ticketService: LogicielCaisseTicketService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.headerService.setConfig({
      title: 'Tickets consolidés',
      buttons: [],
    });

    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const id = Number(params.get('id') || 0);
      const ret = params.get('return');
      if (ret && String(ret).startsWith('/')) {
        this.returnUrl = String(ret);
      }
      this.factureId = Number.isFinite(id) && id > 0 ? id : 0;
      this.factureCode = null;
      this.loadTickets();
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  goBack(): void {
    void this.router.navigateByUrl(this.returnUrl || '/vente/facture/tous');
  }

  private loadTickets(): void {
    this.loadSub?.unsubscribe();

    if (!this.factureId) {
      this.loading = false;
      this.tickets = [];
      this.toastr.warning('Identifiant de facture manquant.', 'Tickets consolidés');
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.loading = false;
      this.tickets = [];
      this.toastr.warning(
        'Connexion logiciel caisse requise pour afficher les tickets consolidés.',
        'Tickets consolidés'
      );
      return;
    }

    this.loading = true;
    this.tickets = [];
    this.loadSub = this.ticketService.getTicketsByErpFacture(this.factureId).subscribe({
      next: (data) => {
        this.factureCode =
          data.facture_erp_code != null && String(data.facture_erp_code).trim() !== ''
            ? String(data.facture_erp_code).trim()
            : null;
        this.tickets = (data.tickets || []).map((raw) => this.mapTicketRow(raw));
        this.loading = false;
      },
      error: (err: unknown) => {
        this.loading = false;
        this.tickets = [];
        const message =
          err instanceof Error ? err.message : 'Chargement des tickets consolidés impossible.';
        this.toastr.error(message, 'Erreur');
      },
    });
  }

  private mapTicketRow(raw: Record<string, unknown>): FactureConsolidTicketRow {
    const totalNum = raw['total'] != null ? Number(raw['total']) : 0;
    const consolide =
      raw['consolide'] === true ||
      raw['consolide'] === 1 ||
      Number(raw['consolide']) > 0;

    return {
      id: Number(raw['id']) || 0,
      numero: raw['numero'] != null ? String(raw['numero']) : '—',
      dateLabel: this.formatDateLabel(raw['date']),
      typeLabel:
        raw['type_label'] != null && String(raw['type_label']).trim() !== ''
          ? String(raw['type_label']).trim()
          : consolide
            ? 'Consolidé'
            : 'Tické',
      consolide,
      entrepot: raw['entrepot'] != null ? String(raw['entrepot']) : '—',
      statut: raw['statut'] != null ? String(raw['statut']) : '—',
      paiement: raw['paiement'] != null ? String(raw['paiement']) : '—',
      client: raw['client'] != null ? String(raw['client']) : '—',
      totalLabel: `${(Number.isFinite(totalNum) ? totalNum : 0).toLocaleString('fr-FR', {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
      })} DT`,
    };
  }

  private formatDateLabel(value: unknown): string {
    if (value == null || value === '') {
      return '—';
    }
    const d = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(d.getTime())) {
      return String(value);
    }
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
